<template>
  <main-section
    :isEditMode="true"
    :isDiff="table.isDiff"
    :isOutdated="isOutdated"
    :itemType="'TABLE'"
    :typename="'table'"
    :item="table"
    :hasTabsInFooter="true"
    :contentHandlesScroll="true"
    :onUpdate="updateTable"
    :onDelete="deleteTable"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    :isEdited="isTableEdited"
  >
    <div v-show="mode === 'details'" slot="fieldset">
      <fieldset :disabled="isOutdated">
        <div
          class="sixteen wide field required"
          :class="{ error: $v.table.name.$error }"
          v-diff-field.disable="tableDiff.name"
        >
          <!-- Name -->
          <label for="tableName">{{ $t('NAME') }}</label>
          <input
            id="tableName"
            type="text"
            :placeholder="$t('NAME')"
            v-model.trim="table.name"
            @click="$v.table.name.$touch()"
          />
        </div>
      </fieldset>
    </div>
    <div v-show="mode === 'data'" slot="fieldset" ref="droptarget" style="height: 100%">
      <data-table
        id="tableData"
        :initialRows="initRows"
        :initialHeader="initHeader"
        :tableUpdatedHandler="tableUpdated"
        :isOutdated="isOutdated"
        :isDiff="table.isDiff"
        :diff="tableDiff.cellsDiff"
      />
    </div>

    <router-link
      slot="left-bottom-menu"
      class="item"
      v-if="table && table.id"
      :to="{ name: 'table', params: { mode: 'data', tableId: table.id } }"
      :active-class="'active'"
    >
      <i class="table icon"></i>
      {{ $t('DATA') }}
    </router-link>
    <router-link
      slot="left-bottom-menu"
      class="item"
      v-if="table && table.id"
      :to="{ name: 'table', params: { mode: 'details', tableId: table.id } }"
      :active-class="'active'"
    >
      <i class="tasks icon"></i>
      {{ $t('DETAILS') }}
    </router-link>

    <div slot="rightBottomMenuSlot1" class="ui compact icon buttons" style="margin-right: 5px;">
      <button
        type="button"
        class="ui green button"
        @click="downloadCSV()"
        :disabled="isOutdated || table.isDiff"
      >
        <i class="download icon"></i>
      </button>
      <button
        type="button"
        class="ui green button"
        @click="uploadCSV()"
        :disabled="isOutdated || table.isDiff"
      >
        <i class="upload icon"></i>
      </button>
      <a ref="downloadLink" href="#" style="display: none;"></a>
      <input ref="uploadButton" type="file" accept=".csv" style="display: none;" />
      <loading-confirm-button
        class="ui icon red button"
        :onClick="deleteTable"
        :confirmMessage="
          $t('DELETE_CONFIRM_MESSAGE', { thingType: $tc('TABLE', 1), thingName: table.name })
        "
        :disabled="isOutdated || table.isDiff"
      >
        <i class="trash icon"></i>
      </loading-confirm-button>
    </div>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

#table-container {
  overflow-y: scroll;
}
#tableData {
  height: 100%;
}
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import papa from 'papaparse'
import { Table, Toast, File, ErrorCodes, UnsavedChecker } from 'services'
import DataTable from './DataTable'
import { LoadingConfirmButton, MainSection } from 'ui'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'table-main', // Note: Cannot use table for main
  mixins: [UnsavedChecker.mixin],
  props: ['tableId', 'mode'],
  components: { DataTable, LoadingConfirmButton, MainSection },

  data() {
    return {
      table: {},
      initRows: [],
      initHeader: [],
      rows: [],
      header: [],
      isOutdated: false,
      tableDiff: {}
    }
  },

  validations: {
    table: {
      name: {
        required
      }
    }
  },

  computed: {
    isTableEdited() {
      return this.generateCSV() !== this.table.csv
    },
    tableData() {
      const data = _.map(_.filter(this.rows, row => !row.deleted), row => _.map(row, d => d.v))
      data.unshift(_.map(this.header, h => h.v))
      return data
    }
  },

  methods: {
    generateCSV() {
      return papa.unparse(this.tableData, {
        header: true,
        delimiter: ',',
        quotes: true
      })
    },

    uploadCSV() {
      this.$refs.uploadButton.click()
    },

    downloadCSV() {
      File.downloadFile(
        this.generateCSV(),
        this.table.name + '.csv',
        'text/csv',
        this.$refs.downloadLink
      )
    },

    tableUpdated(updatedHeader, updatedRows) {
      this.header = updatedHeader
      this.rows = updatedRows
    },

    updateTable() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated && !this.table.isDiff) {
          const newCsv = this.generateCSV()
          return Table.updateTable(this.tableId, this.table, newCsv)
            .then(res => {
              this.table.csv = newCsv
              this.table.version = res.version
              this.initRows = this.rows
              this.initHeader = this.header
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('TABLE', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('TABLE', 1) }),
                    thing: this.$i18n.tc('TABLE', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('TABLE', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('TABLE', 1) }),
              thing: this.$i18n.tc('TABLE', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('TABLE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteTable() {
      Table.deleteTable(this.tableId)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('TABLE', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'tables'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('TABLE', 1) })
            })
          )
          console.error(err)
        })
    },

    readTable(id: string) {
      return Table.readTable(id)
        .then(t => {
          this.table = t
          let tableData = Table.parseCsvToTableData(t.csv)
          this.initRows = tableData.rows
          this.initHeader = tableData.header
          this.rows = this.initRows
          this.header = this.initHeader
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('TABLE', 1) })
            })
          )
          console.error(err)
        })
    },

    processFile(file) {
      if (file.size > 2000000) {
        Toast.error(this.$i18n.t('ERR_FILE_TOO_LARGE', { max: '2MB' }))
        return
      }
      let r = new FileReader()
      r.onload = evt => {
        const csv = papa.parse(evt.target.result).data
        this.initRows = _.map(_.tail(csv), row => {
          return _.map(row, d => {
            return { v: d }
          })
        })
        if (this.initRows.length > 0) {
          this.initHeader = _.map(_.head(csv), h => {
            return { v: h }
          })
        }
        this.rows = this.initRows
        this.header = this.initHeader
        return this.rows
      }
      r.readAsText(file)
    },

    dropFunction(e) {
      if (e.dataTransfer.files.length === 1 && e.dataTransfer.files[0].type === 'text/csv') {
        const file = e.dataTransfer.files[0]
        this.processFile(file)
      }
    },
    onChangeChosen({ changedItem, fullDiff, flaggedDiff }) {
      this.table = changedItem
      let changedTableData = Table.parseCsvToTableData(changedItem.csv)
      this.tableDiff = {
        ...fullDiff,
        cellsDiff: Table.getTableDiff({ rows: this.rows, header: this.header }, changedTableData)
      }
    },
    onRevert(table) {
      this.table = table
      let tableData = Table.parseCsvToTableData(this.table.csv)
      this.rows = tableData.rows
      this.header = tableData.header
      this.tableDiff = {}
    }
  },

  mounted() {
    if (this.$refs.droptarget)
      File.addDropFileFunctionalityToDroptarget(this.$refs.droptarget, this.dropFunction)

    this.$refs.uploadButton.addEventListener('change', e => {
      this.processFile(e.target.files[0])
    })
  },

  created() {
    if (this.$route.params.tableId) this.readTable(this.$route.params.tableId)
  },

  watch: {
    tableId: function(newTableId, oldTableId) {
      if (newTableId != oldTableId) {
        this.readTable(newTableId)
      }
    }
  }
}
</script>
